import React, { useEffect, useState } from "react"
import {
	FileText,
	Zap,
	Check,
	GitBranch,
	Bell,
	Send,
	Search,
	ChevronDown,
	ListChecks,
	Code,
	Info,
	Link2,
	Globe,
	Calendar,
	Server,
	Activity,
	RefreshCw,
	AlertTriangle,
	Plug,
} from "lucide-react"

import "./timeline.scss"

/**
 * Reusable vertical activity/stage timeline. Extracted from the /design sandbox
 * so it can be embedded anywhere (e.g. the add-connector modal). Render
 * <Timeline stages={...} /> for the whole rail, or compose <Stage> directly.
 *
 * Data shape + integration notes: docs/timeline-ui.md.
 */

/**
 * Icon registry — kept explicit so the JSON is safely stateable + we get
 * tree-shaken bundle output. Add new icons here as needed.
 */
export const ICONS = {
	"file-text": FileText,
	zap: Zap,
	"git-branch": GitBranch,
	bell: Bell,
	send: Send,
	search: Search,
	"list-checks": ListChecks,
	code: Code,
	check: Check,
	info: Info,
	// Extras for the connectors flow.
	link: Link2,
	globe: Globe,
	calendar: Calendar,
	server: Server,
	activity: Activity,
	"refresh-cw": RefreshCw,
	"alert-triangle": AlertTriangle,
	plug: Plug,
}

export function Icon({ name, ...rest }) {
	const Component = ICONS[name]
	if (!Component) return null
	return <Component {...rest} />
}

/**
 * Visual pip rendered to the left of each activity row + activity card header.
 * The element-type changes by status, so React naturally remounts and any
 * one-shot enter animation (e.g. the green check's scale-pop) plays cleanly.
 */
export function StatusPip({ status }) {
	if (status === "complete") {
		return (
			<span className="design-check-pip">
				<Check
					size={12}
					strokeWidth={3}
					color="#fff" />
			</span>
		)
	}
	if (status === "active") return <span className="design-spinner" />
	return <span className="design-dashed-ring" />
}

export function HeaderCard({ header }) {
	if (!header) return null
	return (
		<div className="design-card design-card--row">
			<div className="design-card-left">
				<div className="design-icon-tile">
					<Icon
						name={header.icon}
						size={15}
						strokeWidth={1.75}
						color="currentColor" />
				</div>
				<div className="design-card-title">{header.title}</div>
			</div>
			{header.action && (
				<button
					type="button"
					className="design-pill-button"
					onClick={header.action.onClick}>
					{header.action.label}
				</button>
			)}
		</div>
	)
}

/**
 * Renders a badge icon — chooses between the filled glyph treatment
 * (used for Zap so it reads as a solid bolt) and outline-stroke
 * treatment (Check, Info). Filling a stroke-only outline icon collapses
 * it into a solid shape (the bug that turned Info into a blue disk).
 */
function BadgeIcon({ name }) {
	if (!name) return null
	const filled = name === "zap"
	// Info is one pixel smaller than the check — the round outline reads
	// visually a touch heavier at the same nominal size, so we calm it down.
	const size = name === "info" ? 13 : 14
	if (filled) {
		return (
			<Icon
				name={name}
				size={size}
				strokeWidth={0}
				fill="currentColor" />
		)
	}
	return (
		<Icon
			name={name}
			size={size}
			strokeWidth={2.25} />
	)
}

/**
 * A status badge.
 *
 * Two shapes:
 *  - Static:    { icon, label, variant }                  — always shown
 *  - Lifecycle: { phases: [{ duration, ...phaseShape }] } — auto-advances
 *
 * `phases` is an ordered list. Each entry except the last carries a
 * `duration` (ms). Progression is gated on `activated` — phase 0 is held
 * indefinitely until the parent stage activates. Any leading phases with
 * no `duration` are then skipped (so a `pending` first-phase doesn't
 * flash on activation; it serves as the pre-activation marker only).
 *
 * Variants: default | success | info | loading | pending
 */
export function Badge({ badge, activated = true }) {
	const phases = badge.phases || [badge]

	// Initial render: if already activated on mount, skip past any
	// 0-duration leading phases so we don't paint a "pending" frame
	// before transitioning to the real first state.
	const initialIdx = (() => {
		if (!activated) return 0
		let idx = 0
		while (idx < phases.length - 1 && !phases[idx].duration) idx++
		return idx
	})()

	const [phaseIndex, setPhaseIndex] = useState(initialIdx)

	useEffect(() => {
		// Recompute start whenever activation or the badge identity changes.
		let start = 0
		if (activated) {
			while (start < phases.length - 1 && !phases[start].duration) start++
		}
		setPhaseIndex(start)

		if (!activated) return
		if (phases.length <= start + 1) return

		const timeouts = []
		let cumulative = 0
		for (let i = start; i < phases.length - 1; i++) {
			cumulative += phases[i].duration || 0
			const id = setTimeout(() => setPhaseIndex(i + 1), cumulative)
			timeouts.push(id)
		}
		return () => timeouts.forEach(clearTimeout)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [badge, activated])

	const display = phases[phaseIndex]
	const variant = display.variant || "default"
	const cls = ["design-trigger-pill", `design-trigger-pill--${variant}`].join(" ")

	return (
		<div className={cls}>
			{variant === "loading" ? (
				<span className="design-trigger-pill-spinner" />
			) : variant === "pending" ? (
				<span className="design-trigger-pill-pending-pip" />
			) : (
				<BadgeIcon name={display.icon} />
			)}
			<span>{display.label}</span>
		</div>
	)
}

export function TriggerPill({ trigger, activated = true }) {
	if (!trigger) return null
	const list = Array.isArray(trigger) ? trigger : [trigger]
	if (!list.length) return null
	return (
		<div className="design-triggers">
			{list.map((badge, i) => (
				<Badge
					key={badge.id ?? i}
					badge={badge}
					activated={activated} />
			))}
		</div>
	)
}

function ActivityItem({ item }) {
	const cls = ["design-activity-row"]
	if (item.status === "active") cls.push("design-activity-row--active")
	if (item.status === "pending") cls.push("design-activity-row--pending")
	return (
		<li className={cls.join(" ")}>
			<StatusPip status={item.status} />
			<div className="design-activity-body">
				<div className="design-activity-line">{item.content}</div>
				{item.sub && <div className="design-activity-sub">{item.sub}</div>}
			</div>
			{item.duration && <span className="design-duration">{item.duration}</span>}
		</li>
	)
}

export function Summary({ summary }) {
	if (summary == null) return null
	const parts = Array.isArray(summary) ? summary.filter(Boolean) : [summary]
	if (!parts.length) return null
	return (
		<div className="design-summary">
			{parts.map((part, i) => (
				<React.Fragment key={i}>
					{i > 0 && (
						<span
							className="design-summary-dot"
							aria-hidden="true">
							·
						</span>
					)}
					<span className="design-summary-part">{part}</span>
				</React.Fragment>
			))}
		</div>
	)
}

export function ActivityCard({ activity }) {
	const defaultOpen = activity?.status === "active"
	const [open, setOpen] = useState(defaultOpen)

	// Sync to the status-derived default whenever status changes from outside.
	useEffect(() => {
		setOpen(defaultOpen)
	}, [defaultOpen])

	if (!activity) return null
	const meta = activity.meta || {}
	const toggle = () => setOpen((o) => !o)

	return (
		<div className="design-card design-card--activity">
			<button
				type="button"
				className={["design-activity-header", open && "is-open"].filter(Boolean).join(" ")}
				onClick={toggle}>
				<div className="design-activity-header-left">
					<StatusPip status={activity.status} />
					<span className="design-activity-title">{activity.title}</span>
				</div>
				<div className="design-activity-header-right">
					{meta.steps && <span className="design-steps">{meta.steps}</span>}
					{meta.elapsed && <span className="design-elapsed">{meta.elapsed}</span>}
				</div>
				<span className="design-header-chevron-wrap">
					<ChevronDown
						size={14}
						strokeWidth={2}
						className={["design-header-chevron", open && "is-up"].filter(Boolean).join(" ")} />
				</span>
			</button>

			{/* Summary — renders below the header whenever the activity has
			    a summary value and isn't still pending. Always open so any
			    real-time updates (per-item summaryAt writes, completion
			    timestamps) are visible alongside the running items list. */}
			{activity.summary != null && activity.status !== "pending" && (
				<div className="design-activity-collapse design-activity-collapse--summary is-open">
					<div className="design-activity-collapse-inner">
						<Summary summary={activity.summary} />
					</div>
				</div>
			)}

			{/* List — visible when expanded. */}
			<div
				className={[
					"design-activity-collapse",
					"design-activity-collapse--list",
					open && "is-open",
				]
					.filter(Boolean)
					.join(" ")}
				aria-hidden={!open}>
				<div className="design-activity-collapse-inner">
					<div className="design-activity-divider" />
					<ul className="design-activity-list">
						{(activity.items || []).map((item, i) => (
							<ActivityItem
								key={item.id ?? i}
								item={item} />
						))}
					</ul>
				</div>
			</div>
		</div>
	)
}

export function ParallelGroup({ group }) {
	if (!group) return null
	return (
		<div className="design-group">
			<div className="design-group-label">
				<StatusPip status={group.status} />
				<span className="design-group-title">{group.title}</span>
			</div>
			<div className="design-group-cards">
				{(group.activities || []).map((sub, i) => (
					<ActivityCard
						key={sub.id ?? i}
						activity={sub} />
				))}
			</div>
		</div>
	)
}

export function Stage({ stage }) {
	const stageStatus = stage.activity?.status ?? stage.group?.status
	const dim = stageStatus === "pending"
	// A stage is "activated" once it leaves pending — at that point its
	// lifecycle badges should start cycling through their phases.
	const activated = stageStatus === "active" || stageStatus === "complete"
	const cls = ["design-stage", dim && "design-stage--dim"].filter(Boolean).join(" ")
	return (
		<div className={cls}>
			<HeaderCard header={stage.header} />
			<TriggerPill
				trigger={stage.trigger}
				activated={activated} />
			{stage.activity && <ActivityCard activity={stage.activity} />}
			{stage.group && <ParallelGroup group={stage.group} />}
		</div>
	)
}

/**
 * Convenience wrapper that renders a full stage list with the connecting rail.
 * Drop inside a `.design-root` container to inherit the timeline typography.
 */
export function Timeline({ stages }) {
	return (
		<div className="design-stages">
			{(stages || []).map((stage, i) => (
				<Stage
					key={stage.id ?? i}
					stage={stage} />
			))}
		</div>
	)
}

export default Timeline
